import * as SwitchPrimitive from '@radix-ui/react-switch';
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
} from 'react';

import { composeVariants, cva } from '../../utils/cva';
import { useFocusVisible } from '../../utils/use-focus-visible';
import { dataFocusRing } from '../FocusVisible';

const switchRootVariants = composeVariants(
  dataFocusRing,
  cva({
    base: [
      'w-9',
      'h-5',
      'relative',
      'rounded-full',
      'transition-colors',
      'duration-100',
      'bg-content-muted',
      'data-[state=checked]:bg-state-selected',
      'text-surface-default',
      'disabled:!bg-palette-gray-300',
    ],
  }),
);

const switchThumbVariants = cva({
  base: [
    'block',
    'w-4',
    'h-4',
    'rounded-full',
    'bg-current',
    'will-change-transform',
    'translate-x-0.5',
    'data-[state=checked]:translate-x-[18px]',
    'transition-transform',
    'duration-100',
  ],
});

export interface SwitchProps
  extends ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  thumbProps?: ComponentProps<typeof SwitchPrimitive.Thumb>;
  'data-testid'?: string;
}

export const Switch = forwardRef<
  ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>((props, ref) => {
  const {
    className,
    thumbProps = {},
    onFocus,
    onBlur,
    'data-testid': dataTestId = 'switch',
    ...rest
  } = props;

  const { className: thumbClassName, ...thumbRest } = thumbProps;

  return (
    <SwitchPrimitive.Root
      ref={ref}
      className={switchRootVariants({ className })}
      data-testid={dataTestId}
      {...useFocusVisible({ onFocus, onBlur })}
      {...rest}
    >
      <SwitchPrimitive.Thumb
        className={switchThumbVariants({ className: thumbClassName })}
        data-testid={`${dataTestId}-thumb`}
        {...thumbRest}
      />
    </SwitchPrimitive.Root>
  );
});
